import { defineMessages, MessageDescriptor } from "react-intl";
import { ExtraCategory } from "../domain/ExtraCategory.enum";
import { IssueCode } from "../domain/entities/ValidationIssue";

type SingleTextIssueCode = Exclude<IssueCode, IssueCode.CATEGORY_LIMITS_NO_ACTIVE_EXTRA>;

/**
 * One message per issue code, with id `extrasCategories.issue.<CODE>` (the issue `message_key`)
 * and the English text of the requirements as `defaultMessage`. Placeholders are the issue
 * `params`.
 */
export const issueMessages: Record<SingleTextIssueCode, MessageDescriptor> = defineMessages({
  CATEGORY_LIMITS_INCLUDED_REQUIRED: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_INCLUDED_REQUIRED",
    defaultMessage: "Select how many {category} are included in the price.",
  },
  CATEGORY_LIMITS_MAX_REQUIRED: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_MAX_REQUIRED",
    defaultMessage: "Select the maximum number of {category} allowed.",
  },
  CATEGORY_LIMITS_INCLUDED_OUT_OF_RANGE: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_INCLUDED_OUT_OF_RANGE",
    defaultMessage: "Included must be between 0 and 10.",
  },
  CATEGORY_LIMITS_MAX_OUT_OF_RANGE: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_MAX_OUT_OF_RANGE",
    defaultMessage: "Maximum must be between 1 and 10.",
  },
  CATEGORY_LIMITS_INCLUDED_GREATER_THAN_MAX: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_INCLUDED_GREATER_THAN_MAX",
    defaultMessage: "Included cannot be greater than the maximum allowed.",
  },
  CATEGORY_LIMITS_DUPLICATE_BLOCK: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_DUPLICATE_BLOCK",
    defaultMessage: "The {category} block is sent more than once.",
  },
  CATEGORY_LIMITS_UNKNOWN_CATEGORY: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_UNKNOWN_CATEGORY",
    defaultMessage: "The category {category} does not exist.",
  },
  CATEGORY_LIMITS_EXTRA_NO_INVENTORY_LINE: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_EXTRA_NO_INVENTORY_LINE",
    defaultMessage:
      "{extra} does not have any inventory line of this accommodation selected in its configuration, so it will not be offered even if it is ticked here.",
  },
  EXTRA_CATEGORY_UNSUPPORTED_TYPE: {
    id: "extrasCategories.issue.EXTRA_CATEGORY_UNSUPPORTED_TYPE",
    defaultMessage: "Only available for per unit & stay or per unit & night extras.",
  },
  EXTRA_VEHICLE_TYPE_REQUIRED: {
    id: "extrasCategories.issue.EXTRA_VEHICLE_TYPE_REQUIRED",
    defaultMessage: "Select the vehicle type.",
  },
  EXTRA_VEHICLE_FIELDS_WITHOUT_VEHICLE: {
    id: "extrasCategories.issue.EXTRA_VEHICLE_FIELDS_WITHOUT_VEHICLE",
    defaultMessage:
      "The licence plate and the vehicle type are only available for the Vehicle category.",
  },
  EXTRA_VEHICLE_TYPE_INVALID: {
    id: "extrasCategories.issue.EXTRA_VEHICLE_TYPE_INVALID",
    defaultMessage: "The vehicle type {vehicleType} is not valid.",
  },
  EXTRA_CHANGE_STOPS_COUNTING: {
    id: "extrasCategories.issue.EXTRA_CHANGE_STOPS_COUNTING",
    defaultMessage:
      "{extra} will stop counting against the limits of the accommodations listed below.",
  },
  EXTRA_CHANGE_LEAVES_BLOCK_EMPTY: {
    id: "extrasCategories.issue.EXTRA_CHANGE_LEAVES_BLOCK_EMPTY",
    defaultMessage:
      "This change cannot be saved: {extra} is the only active extra selected in the {category} limits of the accommodations listed below. First select another active extra there, or turn the limits off.",
  },
  EXTRA_DEACTIVATION_REMOVES: {
    id: "extrasCategories.issue.EXTRA_DEACTIVATION_REMOVES",
    defaultMessage: "{extra} will no longer be present in the accommodations listed below.",
  },
  EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY: {
    id: "extrasCategories.issue.EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY",
    defaultMessage:
      "The {category} limits of the accommodations listed below will be left without any active extra. Those accommodations cannot be saved until an active extra is selected or the limits are turned off.",
  },
  EXTRA_COVERAGE_LOST: {
    id: "extrasCategories.issue.EXTRA_COVERAGE_LOST",
    defaultMessage:
      "{extra} will no longer be available in the accommodations listed below, because none of their inventory lines is selected.",
  },
  EXTRA_CATEGORY_NOT_ENABLED_IN_PROPERTY: {
    id: "extrasCategories.issue.EXTRA_CATEGORY_NOT_ENABLED_IN_PROPERTY",
    defaultMessage:
      "Extra categories are not enabled in {property}; the category will not apply there until they are.",
  },
});

/** `CATEGORY_LIMITS_NO_ACTIVE_EXTRA` has one text per category. */
export const noActiveExtraMessages: Record<
  ExtraCategory.VEHICLE | ExtraCategory.FACILITIES,
  MessageDescriptor
> = defineMessages({
  VEHICLE: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_NO_ACTIVE_EXTRA.VEHICLE",
    defaultMessage:
      "Select at least one vehicle extra. With none selected, the guest will not be able to declare the vehicle included in the accommodation.",
  },
  FACILITIES: {
    id: "extrasCategories.issue.CATEGORY_LIMITS_NO_ACTIVE_EXTRA.FACILITIES",
    defaultMessage:
      "Select at least one facilities extra. With none selected, the guest will not be able to declare the facility included in the accommodation.",
  },
});
